import React from 'react';
import ReactDOM from 'react-dom';
import { V } from '../utils/theme';
import { Icons } from './Icons';

// Full-screen sheet used by the white-label flows (consent, join landing,
// suspended notice). Portals to document.body, respects the safe areas with
// explicit padding-* properties (never the shorthand) and keeps the footer
// inside the flex column so it never overlaps the content — iOS installed-PWA
// is the target.
export function FullSheet({ title, onClose, closeLabel = "Back", children, footer, zIndex = 9990, ariaLabel, maxWidth = 560 }) {
  return ReactDOM.createPortal(
    <div role="dialog" aria-modal="true" aria-label={ariaLabel || title || "Sheet"} style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex,
      background: `${V.ambient}, ${V.bg}`, color: V.text, fontFamily: V.font,
      display: "flex", flexDirection: "column", animation: "fadeIn .2s ease-out",
    }}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "100%", maxWidth, marginLeft: "auto", marginRight: "auto" }}>
        {(title || onClose) && (
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10,
            paddingTop: "max(16px, env(safe-area-inset-top, 0px))", paddingBottom: 10, paddingLeft: 12, paddingRight: 16 }}>
            {onClose ? (
              <button type="button" onClick={onClose} aria-label={closeLabel} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none",
                cursor: "pointer", paddingTop: 8, paddingBottom: 8, paddingLeft: 4, paddingRight: 6, WebkitTapHighlightColor: "transparent", flexShrink: 0 }}>
                {Icons.chevLeft({ size: 20, color: V.accent })}
                <span style={{ fontSize: 14, color: V.accent, fontWeight: 600 }}>{closeLabel}</span>
              </button>
            ) : <div style={{ width: 8 }} />}
            {title && <div style={{ flex: 1, textAlign: "center", fontSize: 15, fontWeight: 700, color: V.text, paddingRight: onClose ? 56 : 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>}
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
          paddingTop: (title || onClose) ? 8 : "max(16px, env(safe-area-inset-top, 0px))", paddingBottom: 20, paddingLeft: 20, paddingRight: 20 }}>
          {children}
        </div>
        {footer && (
          <div style={{ flexShrink: 0, borderTop: `1px solid ${V.cardBorder}`, background: V.navBg, backdropFilter: "blur(20px)",
            paddingTop: 14, paddingBottom: "max(20px, env(safe-area-inset-bottom, 0px))", paddingLeft: 20, paddingRight: 20 }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Compact accessible switch, same look as the Settings sync toggles
export function Switch({ on, onChange, label, disabled }) {
  const track = V.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)";
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled}
      onClick={() => !disabled && onChange(!on)} style={{
        width: 46, height: 26, borderRadius: 13, border: "none", cursor: disabled ? "default" : "pointer", flexShrink: 0,
        background: on ? V.accent : track, position: "relative", transition: "background .2s", opacity: disabled ? .5 : 1,
        WebkitTapHighlightColor: "transparent", padding: 0,
      }}>
      <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", position: "absolute", top: 3,
        left: on ? 23 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
    </button>
  );
}

// Centered modal (error dialogs) — portalled, explicit paddings, safe-area aware
export function CenterModal({ children, ariaLabel, zIndex = 9992, onBackdrop }) {
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex, display: "flex", alignItems: "center", justifyContent: "center",
      paddingTop: "max(20px, env(safe-area-inset-top, 0px))", paddingBottom: "max(20px, env(safe-area-inset-bottom, 0px))", paddingLeft: 20, paddingRight: 20 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)", animation: "fadeIn .2s ease-out" }} onClick={onBackdrop} />
      <div role="alertdialog" aria-modal="true" aria-label={ariaLabel} style={{ position: "relative", background: V.sheetBg, borderRadius: 22,
        paddingTop: 22, paddingBottom: 22, paddingLeft: 22, paddingRight: 22, maxWidth: 380, width: "100%", border: `1px solid ${V.cardBorder}`,
        boxShadow: V.shadowLg, maxHeight: "85vh", overflowY: "auto", animation: "popIn .25s cubic-bezier(.2,.8,.3,1) both", fontFamily: V.font, color: V.text }}>
        {children}
      </div>
    </div>,
    document.body
  );
}
